import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function LocationDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [location, setLocation] = useState(null);
  const [isEditMode, setIsEditMode] = useState(searchParams.get("edit") === "true");
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchLocation = async () => {
    try {
      const res = await axios.get(`https://api.pdf.tajgateways.com/api-v1/locations/${id}`);
      setLocation(res.data);
    } catch (err) {
      console.error("Error loading location:", err);
      toast.error("Location not found");
      navigate("/admin/locations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchLocation();
  }, [id]);

  if (loading) return <p style={{ padding: 40 }}>Loading...</p>;
  if (!location) return <p style={{ padding: 40 }}>Location not found.</p>;

  return (
    <div style={{ padding: "40px", fontFamily: "Inter, sans-serif" }}>
      {/* Toast Container */}
      <ToastContainer position="top-right" autoClose={2500} />

      <button
        onClick={() => navigate(-1)}
        style={{
          background: "#e5e7eb",
          border: "none",
          borderRadius: "6px",
          padding: "6px 12px",
          marginBottom: "20px",
          cursor: "pointer",
        }}
      >
        ← Back
      </button>

      {isEditMode ? (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            setBtnLoading(true);

            try {
              await axios.put(
                `https://api.pdf.tajgateways.com/api-v1/locations/${id}`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
              );

              await fetchLocation();
              setIsEditMode(false);
              toast.success("✅ Location updated successfully!");
            } catch (err) {
              console.error("Update error:", err);
              toast.error("❌ Failed to update location");
            } finally {
              setBtnLoading(false);
            }
          }}
          style={{
            marginTop: "20px",
            background: "#f9fafb",
            padding: "20px",
            borderRadius: "10px",
          }}
          encType="multipart/form-data"
        >
          <h2 style={{ marginBottom: "15px" }}>Edit Location</h2>

          <label style={{ display: "block", marginBottom: "8px" }}>
            Title:
            <input
              name="title"
              defaultValue={location.title}
              placeholder="Title"
              style={inputStyle}
            />
          </label>

          <label style={{ display: "block", marginBottom: "8px" }}>
            Description:
            <input
              name="description"
              defaultValue={location.description}
              placeholder="Description"
              style={inputStyle}
            />
          </label>

          <label style={{ display: "block", marginBottom: "8px" }}>
            Tag:
            <input
              name="tag"
              defaultValue={location.tag}
              placeholder="Tag"
              style={inputStyle}
            />
          </label>

          <label style={{ display: "block", marginBottom: "8px" }}>
            SIC Price:
            <input
              name="sic_price"
              type="number"
              defaultValue={location.sic_price ?? ""}
              placeholder="SIC Price"
              style={inputStyle}
            />
          </label>

          <label style={{ display: "block", marginBottom: "8px" }}>
            PVT Price:
            <input
              name="pvt_price"
              type="number"
              defaultValue={location.pvt_price ?? ""}
              placeholder="PVT Price"
              style={inputStyle}
            />
          </label>

          <label style={{ display: "block", marginBottom: "8px" }}>
            Child Price:
            <input
              name="child_price"
              type="number"
              defaultValue={location.child_price ?? ""}
              placeholder="Child Price"
              style={inputStyle}
            />
          </label>

          <label style={{ display: "block", marginBottom: "8px" }}>
            Adult Price:
            <input
              name="adult_price"
              type="number"
              defaultValue={location.adult_price ?? ""}
              placeholder="Adult Price"
              style={inputStyle}
            />
          </label>

          <label style={{ display: "block", marginBottom: "8px" }}>
            VIP Price:
            <input
              name="vip_price"
              type="number"
              defaultValue={location.vip_price ?? ""}
              placeholder="VIP Price"
              style={inputStyle}
            />
          </label>

          <label style={{ display: "block", marginBottom: "8px" }}>
            Image 1:
            <input type="file" name="img" accept="image/*" style={inputStyle} />
          </label>

          <label style={{ display: "block", marginBottom: "8px" }}>
            Image 2:
            <input type="file" name="img1" accept="image/*" style={inputStyle} />
          </label>

          <label style={{ display: "block", marginBottom: "8px" }}>
            Image 3:
            <input type="file" name="img2" accept="image/*" style={inputStyle} />
          </label>

          <div style={{ marginTop: "15px" }}>
            <button type="submit" style={submitBtnStyle} disabled={btnLoading}>
              {btnLoading ? "Saving..." : "Save Changes"}
            </button>
            <button
              type="button"
              onClick={() => setIsEditMode(false)}
              style={cancelBtnStyle}
              disabled={btnLoading}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <h1 style={{ fontSize: "2rem", fontWeight: "bold" }}>{location.title}</h1>

          <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
            {[location.img, location.img1, location.img2].map(
              (img, i) =>
                img && (
                  <img
                    key={i}
                    src={
                      img.startsWith("http")
                        ? img
                        : `https://api.pdf.tajgateways.com/${img}`
                    }
                    alt=""
                    style={{
                      width: "300px",
                      height: "200px",
                      objectFit: "cover",
                      borderRadius: "10px",
                    }}
                  />
                )
            )}
          </div>

          <div style={{ marginTop: "20px" }}>
            {(Array.isArray(location.description)
              ? location.description
              : [location.description]
            )?.map((desc, i) => (
              <p key={i} style={{ lineHeight: "1.6" }}>
                {desc}
              </p>
            ))}
          </div>

          <p style={{ marginTop: "20px", fontStyle: "italic" }}>{location.note}</p>

          <div style={{ marginTop: "30px" }}>
            <h3 style={{ fontWeight: "600" }}>Pricing</h3>
            <ul>
              <li>SIC: AED {location.sic_price}</li>
              <li>PVT: AED {location.pvt_price}</li>
              <li>VIP: AED {location.vip_price}</li>
              <li>Adult Price: AED {location.adult_price}</li>
              <li>Child Price: AED {location.child_price}</li>
            </ul>
          </div>

          <div style={{ marginTop: "30px" }}>
            <button onClick={() => setIsEditMode(true)} style={editBtnStyle}>
              ✏️ Edit
            </button>

            <button
              onClick={async () => {
                if (!window.confirm("Delete this location?")) return;
                setDeleteLoading(true);
                try {
                  await axios.delete(
                    `https://api.pdf.tajgateways.com/api-v1/locations/${id}`
                  );
                  toast.success("✅ Location deleted successfully!");
                  setTimeout(() => navigate("/admin/locations"), 1000);
                } catch (err) {
                  console.error("Delete error:", err);
                  toast.error("❌ Failed to delete location");
                } finally {
                  setDeleteLoading(false);
                }
              }}
              style={deleteBtnStyle}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "🗑 Delete"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* Styles */
const inputStyle = {
  width: "100%",
  padding: "8px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  marginTop: "4px",
};

const submitBtnStyle = {
  background: "#2563eb",
  color: "white",
  border: "none",
  borderRadius: "6px",
  padding: "8px 14px",
  cursor: "pointer",
  marginRight: "10px",
};

const cancelBtnStyle = {
  background: "#9ca3af",
  color: "white",
  border: "none",
  borderRadius: "6px",
  padding: "8px 14px",
  cursor: "pointer",
};

const editBtnStyle = {
  background: "#3b82f6",
  color: "white",
  border: "none",
  borderRadius: "6px",
  padding: "8px 14px",
  cursor: "pointer",
  marginRight: "10px",
};

const deleteBtnStyle = {
  background: "#ef4444",
  color: "white",
  border: "none",
  borderRadius: "6px",
  padding: "8px 14px",
  cursor: "pointer",
};
